import React, { CSSProperties } from 'react';

import { Box, TableCell, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { toKebabCase } from '@uplift/utils';
import { Button, Loader, Markdown, ScrollableView } from '@uplift/ui-v2';
import { AgreementTermType, CollaborativeAgreementSigned } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';


export const DetailsCell: React.FC<{ label: string }> = ({ label, children }) => {
  return (
    <TableCell sx={{ px: 0, width: '50%' }}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body1" color="textPrimary">{children}</Typography>
    </TableCell>
  );
};


export const EffectiveDateCell: React.VFC<{ document: CollaborativeAgreementSigned }> = ({ document }) => {
  const timezone = useTimezone();

  return (
    <DetailsCell label="Effective Date">
      {document.counterSignedAt
        ? CalendarDateService.date(document.counterSignedAt).tz(timezone).format('MMM D, YYYY')
        : 'Upon counter-signature'}
    </DetailsCell>
  );
};


export const agreementTermTypeLabelMap: Record<AgreementTermType, { many: string; single: string }> = {
  [AgreementTermType.days]: {
    many: 'Days',
    single: 'Day',
  },
  [AgreementTermType.months]: {
    many: 'Months',
    single: 'Month',
  },
  [AgreementTermType.years]: {
    many: 'Years',
    single: 'Year',
  },
};


const useStyles = makeStyles({ name: 'DocumentLayout' })(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    width: '100%',
    position: 'relative',
    backgroundColor: theme.palette.common.white,
  },
  header: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    minHeight: 56,
    padding: theme.spacing(1, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  title: {
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  reviewSection: {
    maxWidth: 'min(calc(100% - 44px), 850px)',
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(2, 0),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(5, 0),
    }
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
}));


export type DocumentLayoutProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  contentSpacing?: boolean;
  controls?: React.ReactNode;
};


export const DocumentLayout: React.FC<DocumentLayoutProps> = ({ contentSpacing = true, controls, action, ...props }) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.title} data-testid={toKebabCase(props.title?.toString() || 'document-title')}>{props.title}</Typography>
        {action}
      </Box>
      <React.Suspense fallback={<Loader color="secondary" />}>
        <ScrollableView height="100%">
          <Box className={classes.reviewSection}>
            <React.Suspense fallback={<Loader color="secondary" />}>
              <Box className={classes.content}>
                {props.subtitle && <Typography variant="body2" color="textSecondary">{props.subtitle}</Typography>}
                <Box pt={contentSpacing ? 2 : 0}>
                  {props.children}
                </Box>
              </Box>
            </React.Suspense>
          </Box>
        </ScrollableView>
        {controls}
      </React.Suspense>
    </Box>
  );
};


export const useSignatureAnchor = () => {
  const signatureRef = React.useRef<HTMLDivElement>(null);

  const scrollToSignature = React.useCallback(() => {
    signatureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  return { signatureRef, scrollToSignature };
};


export const ReviewAndSignAction: React.VFC<{ onClick: () => void }> = ({ onClick }) => (
  <Button variant="text" color="secondary" onClick={onClick}>
    Review &amp; sign
  </Button>
);


const useAgreementTextStyles = makeStyles({ name: 'AgreementText' })(theme => {
  const clauseHeading = {
    fontWeight: 600,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  };

  return {
    root: {
      '&& h1': { ...theme.typography.h6 as CSSProperties, ...clauseHeading },
      '&& h2': { ...theme.typography.subtitle1 as CSSProperties, ...clauseHeading },
      '&& h3, && h4, && h5, && h6': { ...theme.typography.subtitle2 as CSSProperties, ...clauseHeading },
      '&& h1:first-of-type, && h2:first-of-type, && h3:first-of-type': { marginTop: 0 },
    },
  };
});


export const AgreementText: React.FC<{ content: string }> = ({ content }) => {
  const { classes } = useAgreementTextStyles();

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="overline" color="secondary">AGREEMENT TEXT</Typography>
      <Box className={classes.root}>
        <Markdown>{content}</Markdown>
      </Box>
    </Box>
  );
};
